import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { WorkItemDetail, WorkItemReleases } from "../../shared/types/api";
import { Button, Card, CardBody, CardHeader, StatusBadge } from "../../shared/ui";
import { SetReleaseDrawer } from "./SetReleaseDrawer";
import {
  RELATION_LABELS,
  RELEASE_STATUS_LABELS,
  RELEASE_STATUS_TONE,
} from "../releases/releaseVocabulary";

/**
 * Which releases this work item sits in, and how (§10).
 *
 * <p>§10's point is that "in a release" is not one relationship. A bug can be shipped by 2.1,
 * observed in 2.0 and still affect 1.9, and a single release field forces all three into one
 * answer — usually the wrong one. So each relation is listed with its own kind.
 *
 * <p>Rendered only when there is something to say. An empty panel on every work item in a team
 * that does not use releases is noise on the screen people read most.
 */
export function WorkItemReleasesPanel({
  item,
  onChanged,
}: {
  item: WorkItemDetail;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [changing, setChanging] = useState(false);
  const workItemId = item.id;

  const releases = useQuery({
    queryKey: ["work-item-releases", workItemId],
    queryFn: () => api.get<WorkItemReleases>(`/api/v1/work-items/${workItemId}/releases`),
    enabled: can("RELEASE_VIEW"),
  });

  const relations = releases.data?.relations ?? [];
  if (!can("RELEASE_VIEW")) {
    return null;
  }

  const change = can("WORK_ITEM_EDIT") && (
    <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>
      {item.releaseId ? "Change" : "Set"}
    </Button>
  );

  /*
   * Shown even with nothing to list, unlike most panels here. "Which release is this going in?"
   * is a question people ask of work that has no answer yet, and a panel that only appeared once
   * the answer existed would give them nowhere to set it.
   */
  if (relations.length === 0) {
    return (
      <>
        <Card>
          <CardHeader title="Releases" subtitle="Not in any release" action={change} />
        </Card>
        <SetReleaseDrawer
          item={item}
          open={changing}
          onClose={() => setChanging(false)}
          onSaved={onChanged}
        />
      </>
    );
  }

  return (
    <>
    <Card>
      <CardHeader
        title="Releases"
        subtitle={`In ${relations.length} ${relations.length === 1 ? "release" : "releases"}`}
        action={change}
      />
      <CardBody>
        <ul className="divide-y divide-ink-100">
          {relations.map((relation) => (
            <li
              key={`${relation.releaseId}-${relation.relationType}`}
              className="flex items-center justify-between gap-3 py-2"
            >
              <button
                type="button"
                className="min-w-0 text-left"
                onClick={() => navigate(`/releases/${relation.releaseId}`)}
              >
                <p className="truncate text-sm font-medium text-ink-900 hover:underline">
                  {relation.version}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">
                  <span className="font-mono">{relation.releaseKey}</span>
                  {" · "}
                  {RELATION_LABELS[relation.relationType]}
                </p>
              </button>
              <StatusBadge tone={RELEASE_STATUS_TONE[relation.status]}>
                {RELEASE_STATUS_LABELS[relation.status]}
              </StatusBadge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
    <SetReleaseDrawer
      item={item}
      open={changing}
      onClose={() => setChanging(false)}
      onSaved={onChanged}
    />
    </>
  );
}
